import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Trophy, Target, Flame, TrendingUp, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

export default function PlayerStatsSection({ userId }: { userId?: string }) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const playerId = userId || user?.id;
  const [period, setPeriod] = useState<"total" | "month">("total");

  const { data: stats, isLoading } = useQuery({
    queryKey: ["player-stats", playerId, organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("player_stats_view")
        .select("*")
        .eq("player_id", playerId!)
        .eq("organization_id", organization!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!playerId && !!organization?.id,
  });

  const { data: recentMatches = [] } = useQuery({
    queryKey: ["match-results-stats", playerId, organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("match_results")
        .select("*")
        .eq("organization_id", organization!.id)
        .or(`winner_team.cs.{${playerId}},loser_team.cs.{${playerId}}`)
        .order("played_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []);
    },
    enabled: !!playerId && !!organization?.id,
  });

  // Level history from level_assessments
  const { data: levelHistory = [] } = useQuery({
    queryKey: ["level-history", playerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("level_assessments")
        .select("suggested_level, created_at")
        .eq("assessed_user_id", playerId!)
        .order("created_at", { ascending: true })
        .limit(20);
      if (error) throw error;
      return (data ?? []).map((d) => ({
        date: new Date(d.created_at).toLocaleDateString("sv-SE", { month: "short", day: "numeric" }),
        level: d.suggested_level,
      }));
    },
    enabled: !!playerId,
  });

  if (isLoading) return <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-24" />)}</div>;

  // Calculate month stats from recentMatches
  const now = new Date();
  const monthMatches = recentMatches.filter((m) => {
    const d = new Date(m.played_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthWins = monthMatches.filter((m) => (m.winner_team as string[]).includes(playerId!)).length;
  const monthLosses = monthMatches.length - monthWins;
  const monthWinRate = monthMatches.length > 0 ? Math.round((monthWins / monthMatches.length) * 100) : 0;

  const totalMatches = period === "month" ? monthMatches.length : Number(stats?.total_matches ?? 0);
  const wins = period === "month" ? monthWins : Number(stats?.wins ?? 0);
  const losses = period === "month" ? monthLosses : Number(stats?.losses ?? 0);
  const winRate = period === "month" ? monthWinRate : Number(stats?.win_rate ?? 0);
  const bestStreak = Number(stats?.best_streak ?? 0);

  const pieData = totalMatches > 0
    ? [
        { name: "Vinster", value: wins, color: "hsl(var(--primary))" },
        { name: "Förluster", value: losses, color: "hsl(var(--muted))" },
      ]
    : [];

  return (
    <div className="space-y-4">
      {/* Period toggle */}
      <div className="flex gap-1 rounded-lg bg-muted p-1">
        <Button
          variant="ghost"
          size="sm"
          className={cn("flex-1 h-8 text-xs", period === "total" && "bg-background shadow-sm")}
          onClick={() => setPeriod("total")}
        >
          Totalt
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn("flex-1 h-8 text-xs gap-1", period === "month" && "bg-background shadow-sm")}
          onClick={() => setPeriod("month")}
        >
          <Calendar className="h-3 w-3" /> Denna månad
        </Button>
      </div>

      {/* Quick stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Trophy className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalMatches}</p>
              <p className="text-xs text-muted-foreground">Spelade matcher</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Target className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{winRate}%</p>
              <p className="text-xs text-muted-foreground">Vinstprocent</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Flame className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{wins}</p>
              <p className="text-xs text-muted-foreground">Vinster</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{period === "total" ? bestStreak : losses}</p>
              <p className="text-xs text-muted-foreground">{period === "total" ? "Längsta svit" : "Förluster"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Win/Loss pie chart */}
      {totalMatches > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Vinster / Förluster</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Level progression */}
      {levelHistory.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Nivåutveckling</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={levelHistory}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} className="text-muted-foreground" />
                  <YAxis domain={[1, 10]} tick={{ fontSize: 10 }} className="text-muted-foreground" />
                  <Tooltip />
                  <Line type="monotone" dataKey="level" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {totalMatches === 0 && (
        <p className="text-center text-muted-foreground text-sm py-6">
          Ingen matchstatistik ännu. Rapportera ditt första resultat!
        </p>
      )}
    </div>
  );
}
